import { useEffect, useRef } from 'react';
import { cn } from '../lib/cn';

export interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  error?: string | null;
  'aria-label'?: string;
}

/** Поле ввода кода подтверждения: отдельные ячейки по числу цифр (макет РТК). */
export function OtpInput({
  value,
  onChange,
  length = 6,
  disabled,
  autoFocus,
  error,
  'aria-label': ariaLabel,
}: OtpInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = value.padEnd(length, ' ').slice(0, length).split('');

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  const setDigits = (next: string[]) => {
    onChange(next.join('').replace(/\s/g, '').slice(0, length));
  };

  const focusAt = (index: number) => {
    const target = refs.current[Math.min(Math.max(index, 0), length - 1)];
    target?.focus();
    target?.select();
  };

  const handleChange = (index: number, raw: string) => {
    const typed = raw.replace(/\D/g, '');
    if (!typed) return;
    const next = [...digits];
    // Ввод нескольких цифр сразу (автоподстановка кода из SMS/почты) раскладываем по ячейкам.
    typed.split('').forEach((char, offset) => {
      if (index + offset < length) next[index + offset] = char;
    });
    setDigits(next);
    focusAt(index + typed.length);
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      const next = [...digits];
      if (next[index]?.trim()) {
        next[index] = ' ';
        setDigits(next);
      } else if (index > 0) {
        next[index - 1] = ' ';
        setDigits(next);
        focusAt(index - 1);
      }
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusAt(index - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusAt(index + 1);
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '');
    if (!pasted) return;
    event.preventDefault();
    onChange(pasted.slice(0, length));
    focusAt(pasted.length);
  };

  return (
    <div>
      <div className="flex justify-center gap-2 sm:justify-start" role="group" aria-label={ariaLabel}>
        {digits.map((digit, index) => (
          <input
            key={`otp-${String(index)}`}
            ref={(el) => {
              refs.current[index] = el;
            }}
            value={digit.trim()}
            onChange={(e) => {
              handleChange(index, e.target.value);
            }}
            onKeyDown={(e) => {
              handleKeyDown(index, e);
            }}
            onPaste={handlePaste}
            onFocus={(e) => {
              e.target.select();
            }}
            disabled={disabled}
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            aria-invalid={error ? true : undefined}
            className={cn(
              'h-12 w-11 rounded-xl border bg-page text-center text-lg font-semibold text-fg caret-brand outline-none transition-colors',
              'focus:border-brand focus:bg-surface disabled:opacity-60',
              error ? 'border-danger' : 'border-transparent'
            )}
          />
        ))}
      </div>
      {error && <p className="mt-2 text-xs text-danger">{error}</p>}
    </div>
  );
}
